import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import SectionProgressGraph from "./Graphs/SectionProgressGraph";
import PillarPerformanceGraph from "./Graphs/PillarPerformanceGraph";

const RANGE_OPTIONS = [
  { label: "3M", value: "3m", bars: 7 },
  { label: "6M", value: "6m", bars: 7 },
  { label: "1Y", value: "1y", bars: 7 },
  { label: "All", value: "all", bars: 10 },
];

// ---------- helpers (logic only, no UI impact) ----------
const getLatestEstimate = (history, section) => {
  const entries = Object.values(history || {});
  if (!entries.length) return null;

  const latest = entries.sort((a, b) =>
    b.date.localeCompare(a.date)
  )[0];

  return latest?.[section.toLowerCase()] ?? null;
};

const EstimateSummary = ({
  section,
  pillars = [],
  pillarEstimates = {},
  estimateHistory = {},
  onSelectPillar,
}) => {
  const [range, setRange] = useState("6m");

  /* ---------- current score ---------- */
  const current = useMemo(
    () => getLatestEstimate(estimateHistory, section),
    [estimateHistory, section]
  );

  /* ---------- pillar graph data ---------- */
  const graphPillars = useMemo(
    () =>
      pillars.map((p) => {
        const est = pillarEstimates[p.id];
        return {
          id: p.id,
          name: p.name,
          score: est?.est ?? null,
          low: est?.low ?? null,
          high: est?.high ?? null,
        };
      }),
    [pillars, pillarEstimates]
  );

  return (
    <Box
      sx={{
        mb: 4,
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(8px)",
        borderRadius: 3,
        px: 3,
        py: 3,
        boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 4,
          alignItems: "stretch",
          flexWrap: "wrap",
        }}
      >
        {/* SUMMARY COLUMN */}
        <Box
          sx={{
            minWidth: 240,
            maxWidth: 300,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            mt: "35px",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Current {section} Estimate
          </Typography>

          <Typography
            variant="h2"
            fontWeight={600}
            sx={{ lineHeight: 1.05, mt: 1 }}
          >
            {current?.est ?? "—"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.75 }}
          >
            Likely range:{" "}
            <strong>
              {current?.low ?? "—"}–{current?.high ?? "—"}
            </strong>
          </Typography>

          <ToggleButtonGroup
            size="small"
            value={range}
            exclusive
            onChange={(_, val) => val && setRange(val)}
            sx={{
              mt: 2.5,
              background: "rgba(0,0,0,0.06)",
              borderRadius: 2,
              p: 0.25,
            }}
          >
            {RANGE_OPTIONS.map((opt) => (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                disableRipple
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.5,
                  border: "none",
                  borderRadius: 1.5,
                  fontSize: "0.8rem",
                  "&.Mui-selected": {
                    background: "#fff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                  },
                }}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* GRAPHS ROW */}
        <Box
          sx={{
            flex: "1 1 600px",
            display: "flex",
            gap: 3,
            alignItems: "stretch",
            minWidth: 320,
          }}
        >
          {/* Progress Graph */}
          <Box sx={{ flex: 1.5 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 1, ml: 3.5 }}
            >
              {section} Progress
            </Typography>

            <Box sx={{ height: 220 }}>
              <SectionProgressGraph
                range={range}
                history={estimateHistory}
                section={section.toLowerCase()}
              />
            </Box>
          </Box>

          {/* Pillar Performance Graph */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 1, ml: 3.5 }}
            >
              {section} Pillar Performance
            </Typography>

            <Box sx={{ height: 220 }}>
              <PillarPerformanceGraph
                pillars={graphPillars}
                onPillarClick={onSelectPillar}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EstimateSummary;
